/**
 * AppContext - 전역 앱 상태 관리
 *
 * 언어, 통화, 번역 등 앱 전체에서 필요한 상태를 제공합니다.
 * 모든 라우트에서 사용 가능합니다.
 *
 * @see SPEC-192-i18n-api.md - Accept-Language 헤더 지원
 */
import { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { Currency } from '@/types';
import type { TranslationStrings } from '@/i18n/translations';
import { getTranslation } from '@/i18n';
import { logger } from '@/utils/logger';
import {
  type Locale,
  type Language,
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  isValidLocale,
  detectBrowserLocale,
  isRtlLocale,
} from '@/config/locales';
import { apiClient } from '@/lib/api/client';
import { setApiErrorLanguage } from '@/lib/api/error-messages';

/**
 * Locale에서 Language로 변환 (기존 UI 번역 호환)
 * 지원되지 않는 Locale은 'es'로 fallback
 */
function localeToLanguage(locale: Locale): Language {
  if (locale === 'ko' || locale === 'es' || locale === 'en') {
    return locale;
  }
  return 'es';
}

/**
 * 초기 로케일 결정 (우선순위: localStorage > navigator)
 *
 * #459 (Q12): localStorage 최우선 — 사용자가 명시적으로 선택한 언어 영속화 보장.
 *   - 새로고침 시 직전 선택 언어 유지 (이전 동작: 멕시코 도메인이면 무조건 es 되돌림)
 *   - 첫 방문 (localStorage 없음): navigator.language 기반 자동 감지
 *
 * #459 변경: 사용자가 *직접 선택* 했을 때만 localStorage 채택 (사용자 의도 = 누설 우려 0).
 * #44 재오픈: QR 첫 진입에서도 지원 브라우저 언어를 채택하고, 미지원 언어는 en fallback.
 *
 * @see Stage 1 (#386) — 한국어 fallback 차단
 * @see Stage 2 (#387) — detectBrowserLocale 이력
 * @see Stage 3 (#388) — getInitialLocale 이력
 * @see #459 (Q12) — localStorage 영속화 최우선
 */
function getInitialLocale(): Locale {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }

  // #459 (Q12): localStorage 최우선 — 사용자 명시적 선택 영속화
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && isValidLocale(stored)) {
      return stored;
    }
  } catch (error) {
    logger.warn('[AppContext] Failed to read locale from localStorage:', error);
  }

  return detectBrowserLocale();
}

interface AppContextValue {
  // 로케일 (API Accept-Language 헤더용, 11개 언어)
  locale: Locale;
  setLocale: (locale: Locale) => void;
  // 언어 (기존 UI 번역용, 3개 언어 호환)
  language: Language;
  setLanguage: (lang: Language) => void;
  // 통화 (#473 R1: setCurrency 제거 — 호출처 0건. 매장별 통화는 StorePricingContext 가 별도 관리)
  currency: Currency;
  // 번역
  t: TranslationStrings;
}

const AppContext = createContext<AppContextValue | null>(null);

interface AppProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
  initialCurrency?: Currency;
}

export function AppProvider({
  children,
  initialLocale,
  initialCurrency = 'MXN',
}: AppProviderProps) {
  // 로케일 상태 (초기값: 제공된 값 > localStorage > 브라우저 감지 > 기본값)
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? getInitialLocale);

  // 기존 UI 번역용 언어 (locale에서 파생)
  const language = useMemo(() => localeToLanguage(locale), [locale]);

  // #473 (R1): currency state 는 useApp() 컨슈머 (CartModal / OrderHistoryPage / useStoreMenus 등) 에서
  // 읽기 전용으로 사용. setter 호출처 cross-file grep 0건이어서 setCurrency 액션 제거.
  // 매장별 통화 변경은 StorePricingContext.setStorePricing 이 담당.
  const [currency] = useState<Currency>(initialCurrency);

  // 번역 객체 메모이제이션
  // PR #476 R3 시정: getTranslation(locale) 사용 — 15 locale 전체 지원.
  // 이전: translations[language] 는 'ko'/'es'/'en' 3 lang 만 cover → 신규 12 locale 사용자
  //       선택 시 silent es fallback 발생 (런타임 함정).
  // 이후: getTranslation 이 partial 번역 + en top-level/leaf 단위 fallback 처리 (translations.ts:2233).
  const t = useMemo(() => getTranslation(locale), [locale]);

  // 로케일 변경 핸들러 (localStorage persist + API 클라이언트 동기화)
  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    } catch (error) {
      logger.warn('[AppContext] Failed to save locale to localStorage:', error);
    }
    apiClient.setLocale(newLocale);
    // API 에러 메시지 i18n 모듈 싱글톤 동기화 — React 외부 (api/client.ts) 에서 같은 언어 사용.
    // #522: error-messages 가 15 locale 전수 지원 → localeToLanguage 축약 없이 locale 직접 전달.
    setApiErrorLanguage(newLocale);
  }, []);

  // 기존 Language 기반 핸들러 (하위 호환)
  const setLanguage = useCallback(
    (lang: Language) => {
      setLocale(lang);
    },
    [setLocale]
  );

  // 마운트 시 API 클라이언트에 초기 로케일 설정
  const isLocaleInitializedRef = useRef(false);

  useEffect(() => {
    if (isLocaleInitializedRef.current) return;
    isLocaleInitializedRef.current = true;

    apiClient.setLocale(locale);
    // #522: 15 locale 전수 지원 — locale 직접 전달
    setApiErrorLanguage(locale);
  }, [locale]);

  /**
   * Stage 2/3 보강 (#400): locale 변경 시 <html lang> 동기화
   * #464 보강: RTL locale 선택 시 <html dir> 동기화
   *
   * Playwright dev audit 발견 — index.html 의 static `lang="en"` 잔존 문제 시정.
   * AppContext locale state 와 동일 우선순위로 `document.documentElement.lang` 도
   * 갱신해야 SEO / 접근성 / 자동 번역 도구 정합.
   * Arabic 처럼 RTL direction 이 필요한 locale 은 `dir` 도 함께 갱신해 전역 레이아웃 방향을 맞춘다.
   *
   * SSR-safe: typeof document undefined 체크 (Vite SSR 또는 테스트 환경 호환).
   *
   * @see #400 — Playwright dev audit 발견
   * @see Stage 2 (#387) detectBrowserLocale
   * @see Stage 3 (#388) getInitialLocale
   */
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
      document.documentElement.dir = isRtlLocale(locale) ? 'rtl' : 'ltr';
    }
  }, [locale]);

  // Context 값 메모이제이션 (리렌더 최소화)
  const value = useMemo<AppContextValue>(
    () => ({
      locale,
      setLocale,
      language,
      setLanguage,
      currency,
      t,
    }),
    [locale, setLocale, language, setLanguage, currency, t]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/**
 * useApp - AppContext 사용 훅
 *
 * @throws AppProvider 외부에서 사용 시 에러
 */
export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

/**
 * useLocale - 로케일 관련 상태 (API Accept-Language 헤더용)
 */
export function useLocale() {
  const { locale, setLocale } = useApp();
  return { locale, setLocale };
}

/**
 * useLanguage - 언어 관련 상태만 사용
 * (기존 UI 번역 호환 + 로케일 통합)
 */
export function useLanguage() {
  const { locale, setLocale, language, setLanguage, t } = useApp();
  return { locale, setLocale, language, setLanguage, t };
}

/**
 * useCurrency - 통화 관련 상태만 사용
 *
 * #473 (R1): setCurrency 제거 — 호출처 cross-file grep 0건.
 * 매장별 통화 변경은 StorePricingContext.setStorePricing 가 담당.
 */
export function useCurrency() {
  const { currency } = useApp();
  return { currency };
}

// Locale 타입 재export (편의성)
export type { Locale };
